import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Column, Row } from 'further-ui/components/Layout';
import { type Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import { AccountingReportDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import PageFilters from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import DatePresetFilter, {
  accountingDateLabels,
} from '../components/DatePresetFilter';
import Alert from '@mui/material/Alert';
import { nonNullable } from 'further-types/utils';

type BalanceSheetRow = {
  id: string;
  label: string;
  periodEnding: number;
  priorYearEnd: number;
};

const pageId = 'balance-sheet';

const TotalText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'bold',
})<{ bold?: boolean }>(({ theme, bold = false }) => ({
  fontWeight: bold ? 700 : 500,
  paddingTop: theme.spacing(theme.spacingSizes.xxs),
  paddingBottom: theme.spacing(theme.spacingSizes.xxs),
}));

const BalanceSheet: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: AccountingReportDateOption;
    customDate?: CalendarDay;
  }>(pageId, {
    defaultFilters: {
      dateOption: AccountingReportDateOption.EndLastQuarter,
      customDate: undefined,
    },
  });
  const { dateOption, customDate } = filters;
  const isCustomDate = dateOption === AccountingReportDateOption.Custom;
  const isCustomDateUnset = isCustomDate && !customDate;

  const {
    data: balanceSheet,
    isLoading,
    isFetching,
  } = api.generalLedger.balanceSheet.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    },
    {
      enabled: showOtherAccounting && !!selectedVehicleId && !isCustomDateUnset,
    },
  );
  const current = balanceSheet?.current;
  const prior = balanceSheet?.prior;

  const exportBalanceSheet = api.generalLedger.exportBalanceSheet.useMutation(
    useHandleTrpcMutation<{ file: string; fileName: string }>()({
      refetchType: 'none',
      onSuccess: ({ file, fileName }) => {
        downloadXlxs(file, fileName);
      },
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (isCustomDateUnset) return;
    exportBalanceSheet.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;

  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  const columns: Array<TableColumn<BalanceSheetRow>> = [
    {
      key: 'label',
      label: 'Accounts',
      width: 250,
      textOverflowEllipsis: true,
    },
    {
      key: 'periodEnding',
      label: `Period ending (${
        balanceSheet?.currentPeriodEnd?.toReadable() ?? ''
      })`,
      width: 125,
      noWrap: true,
      render: ({ periodEnding }) => formatValue(periodEnding),
    },
    {
      key: 'priorYearEnd',
      label: `Prior year end (${
        balanceSheet?.priorYearEnd?.toReadable() ?? ''
      })`,
      width: 125,
      noWrap: true,
      render: ({ priorYearEnd }) => formatValue(priorYearEnd),
    },
  ];

  const renderRow = (
    id: string,
    label: string,
    periodEnding = 0,
    priorYearEnd = 0,
  ): BalanceSheetRow | undefined =>
    periodEnding !== 0 || priorYearEnd !== 0
      ? { id, label, periodEnding, priorYearEnd }
      : undefined;

  const investmentRows = [
    renderRow(
      'investmentsAtCost',
      'Investments at cost',
      current?.investmentsAtCost,
      prior?.investmentsAtCost,
    ),
    renderRow(
      'unrealisedGainLoss',
      'Unrealised gain/loss on investments',
      current?.unrealisedGainLoss,
      prior?.unrealisedGainLoss,
    ),
  ].filter(nonNullable);
  const noInvestments = investmentRows.length === 0;

  const assetRows = [
    renderRow(
      'cashAndCashEquivalents',
      'Cash and cash equivalents',
      current?.cashAndCashEquivalents,
      prior?.cashAndCashEquivalents,
    ),
    renderRow(
      'receivablesAndPrepayments',
      'Receivables and prepayments',
      current?.receivablesAndPrepayments,
      prior?.receivablesAndPrepayments,
    ),
    renderRow(
      'accruedIncome',
      'Accrued income',
      current?.accruedIncome,
      prior?.accruedIncome,
    ),
    renderRow(
      'capitalDueFromPartners',
      'Capital due from partners',
      current?.capitalDueFromPartners,
      prior?.capitalDueFromPartners,
    ),
  ].filter(nonNullable);
  const noAssets = assetRows.length === 0;

  const liabilityRows = [
    renderRow(
      'accountsPayable',
      'Accounts payable',
      current?.accountsPayable,
      prior?.accountsPayable,
    ),
    renderRow(
      'accruedExpenses',
      'Accrued expenses',
      current?.accruedExpenses,
      prior?.accruedExpenses,
    ),
    renderRow(
      'capitalDueToPartners',
      'Capital due to partners',
      current?.capitalDueToPartners,
      prior?.capitalDueToPartners,
    ),
  ].filter(nonNullable);
  const noLiabilities = liabilityRows.length === 0;

  const partnerRows = [
    renderRow(
      'generalPartnerCapital',
      'General partner',
      current?.generalPartnerCapital,
      prior?.generalPartnerCapital,
    ),
    renderRow(
      'carryPartnerCapital',
      'Carry partner',
      current?.carryPartnerCapital,
      prior?.carryPartnerCapital,
    ),
    renderRow(
      'limitedPartnersCapital',
      'Limited partners',
      current?.limitedPartnersCapital,
      prior?.limitedPartnersCapital,
    ),
  ].filter(nonNullable);
  const noPartners = partnerRows.length === 0;

  return (
    <Column spacing="lg" fullHeight>
      <Row centered spacing="sm" justify="space-between" breakpoint="md">
        <Column spacing="xs">
          <Typography variant="h5" color="grey.900">
            Balance sheet
          </Typography>
          <Typography variant="body2" noWrap={!isMobile}>
            {vehicle.vehicleName} as at{' '}
            {isCustomDate
              ? isCustomDateUnset
                ? '...'
                : customDate?.toReadable()
              : accountingDateLabels[dateOption].toLowerCase()}
            {isCustomDate ? (isCustomDateUnset ? '' : '.') : '.'}
          </Typography>
        </Column>
        <PageFilters
          justify="flex-end"
          align="center"
          actions={[
            {
              label: 'Export',
              variant: 'contained',
              onClick: handleExport,
              disabled: isCustomDateUnset,
            },
          ]}
        >
          <DatePresetFilter
            dateOption={dateOption}
            customDate={customDate}
            onChange={(dateOption, customDate) =>
              handleChangeFilters({ dateOption, customDate })
            }
          />
        </PageFilters>
      </Row>
      {balanceSheet && !balanceSheet.isBalanced && !isFetching && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Balance sheet does not balance. Net assets (
          {numberToCurrencyString(balanceSheet.current.netAssets, {
            currency: vehicle?.baseCurrency,
          })}
          ) do not equal partners' capital (
          {numberToCurrencyString(balanceSheet.current.totalPartnersCapital, {
            currency: vehicle?.baseCurrency,
          })}
          ). This indicates a general ledger data integrity issue that needs
          investigation.
        </Alert>
      )}
      <SyncScrollProvider>
        <Column fullHeight>
          <AccountingTable
            columns={columns}
            rows={[]}
            isLoading={isFetching}
            borderless
          />
          {isFetching ? null : (
            <>
              <AccountingSection
                title="Assets"
                titleVariant="h6"
                expandable={false}
                noTopBorder
              >
                <AccountingTable
                  columns={columns}
                  rows={investmentRows}
                  bottomBorderOnly
                  hideHeader
                  customFooterRow={[
                    {
                      key: 'investments-at-fair-value',
                      content: (
                        <Typography
                          variant="h6"
                          paddingLeft={(theme) =>
                            theme.spacing(theme.spacingSizes.xs)
                          }
                        >
                          Investments at fair value
                        </Typography>
                      ),
                      backgroundColor: '#EAF6EE80',
                    },
                    {
                      key: 'periodEnding',
                      content: (
                        <Typography variant="h6">
                          {formatValue(current?.investmentsAtFairValue ?? 0)}
                        </Typography>
                      ),
                      backgroundColor: '#EAF6EE80',
                    },
                    {
                      key: 'priorYearEnd',
                      content: (
                        <Typography variant="h6">
                          {formatValue(prior?.investmentsAtFairValue ?? 0)}
                        </Typography>
                      ),
                      backgroundColor: '#EAF6EE80',
                    },
                  ]}
                />
                <AccountingTable
                  columns={columns}
                  rows={assetRows}
                  emptyMessage="No assets."
                  bodyRowPaddingY={noInvestments && noAssets ? 30 : undefined}
                  noPaddingX={noInvestments && noAssets}
                  borderless
                  hideHeader
                  customFooterRow={[
                    {
                      key: 'total-assets',
                      content: <TotalText variant="h6">Total assets</TotalText>,
                      backgroundColor: grey[50],
                    },
                    {
                      key: 'periodEnding',
                      content: (
                        <TotalText variant="h6">
                          {formatValue(current?.totalAssets ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[50],
                    },
                    {
                      key: 'priorYearEnd',
                      content: (
                        <TotalText variant="h6">
                          {formatValue(prior?.totalAssets ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[50],
                    },
                  ]}
                />
              </AccountingSection>
              <AccountingSection
                title="Liabilities"
                titleVariant="h6"
                expandable={false}
                noTopBorder
                customRow={
                  <AccountingTable
                    columns={columns}
                    rows={[]}
                    topBorderOnly
                    hideHeader
                    displayFooterIfEmptyTable
                    customFooterRow={[
                      {
                        key: 'net-assets',
                        content: (
                          <TotalText variant="h6" bold>
                            Net assets
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                      {
                        key: 'periodEnding',
                        content: (
                          <TotalText variant="h6" bold>
                            {formatValue(current?.netAssets ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                      {
                        key: 'priorYearEnd',
                        content: (
                          <TotalText variant="h6" bold>
                            {formatValue(prior?.netAssets ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                    ]}
                  />
                }
              >
                <AccountingTable
                  columns={columns}
                  rows={liabilityRows}
                  emptyMessage="No liabilities."
                  bodyRowPaddingY={noLiabilities ? 30 : undefined}
                  noPaddingX={noLiabilities}
                  borderless
                  hideHeader
                  customFooterRow={[
                    {
                      key: 'total-liabilities',
                      content: (
                        <TotalText variant="h6">Total liabilities</TotalText>
                      ),
                      backgroundColor: grey[50],
                    },
                    {
                      key: 'periodEnding',
                      content: (
                        <TotalText variant="h6">
                          {formatValue(current?.totalLiabilities ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[50],
                    },
                    {
                      key: 'priorYearEnd',
                      content: (
                        <TotalText variant="h6">
                          {formatValue(prior?.totalLiabilities ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[50],
                    },
                  ]}
                />
              </AccountingSection>
              <AccountingSection
                title="Partners' capital"
                titleVariant="h6"
                expandable={false}
                noTopBorder
                customRow={
                  <AccountingTable
                    columns={columns}
                    rows={[]}
                    topBorderOnly
                    hideHeader
                    displayFooterIfEmptyTable
                    customFooterRow={[
                      {
                        key: 'total-partners',
                        content: (
                          <TotalText variant="h6" bold>
                            Total partners' capital
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                      {
                        key: 'periodEnding',
                        content: (
                          <TotalText variant="h6" bold>
                            {formatValue(current?.totalPartnersCapital ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                      {
                        key: 'priorYearEnd',
                        content: (
                          <TotalText variant="h6" bold>
                            {formatValue(prior?.totalPartnersCapital ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[100],
                      },
                    ]}
                  />
                }
              >
                <AccountingTable
                  columns={columns}
                  rows={partnerRows}
                  emptyMessage="No partners' capital."
                  bodyRowPaddingY={noPartners ? 30 : undefined}
                  noPaddingX={noPartners}
                  borderless
                  hideHeader
                />
              </AccountingSection>
            </>
          )}
        </Column>
      </SyncScrollProvider>
    </Column>
  );
};

export default BalanceSheet;
